import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowLeft, Upload, Sparkles, Trash2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import AppHeader from '../components/AppHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PhotoGallery() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    try {
      const { data } = await axios.get(`${API}/photos/list`, { withCredentials: true });
      setPhotos(data);
    } catch (error) {
      console.error('Error fetching photos:', error);
    }
    setLoading(false);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/photos/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Photo uploaded successfully!');
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to upload photo');
    }
    setUploading(false);
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }
    if (!acknowledged) {
      toast.error('Please acknowledge the AI usage policy');
      return;
    }

    setGenerating(true);
    try {
      await axios.post(`${API}/photos/generate`, { prompt }, { withCredentials: true });
      toast.success('AI photo generated! Look for the "Made with AI" label.');
      setPrompt('');
      fetchPhotos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate photo');
    }
    setGenerating(false);
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await axios.delete(`${API}/photos/${photoId}`, { withCredentials: true });
      toast.success('Photo deleted');
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F7F3]">
      <AppHeader />
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="mb-4" data-testid="back-to-dashboard-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />Back to Dashboard
            </Button>
          </Link>
          <h1 className="text-4xl font-light tracking-tight mb-2" style={{fontFamily: 'Spectral, serif'}}>Memory Photos</h1>
          <p className="text-base text-[#737373]">Upload your own photos or create memory-style AI photos.</p>
        </div>

        <div className="mb-8 bg-[#F5EBE0] border border-[#B94A3E]/20 rounded-md p-4 flex items-start gap-3" data-testid="ai-disclaimer">
          <AlertCircle className="w-5 h-5 text-[#B94A3E] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-[#1A1A1A]/85">
            <strong>Note:</strong> AI-generated photos are labeled "Made with AI" so your recipient knows they're synthetic. Only upload photos you own or have permission to use.
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          <div className="bg-white border border-[#E5E0D8] rounded-md p-8 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
            <div className="flex items-center gap-3 mb-6">
              <Upload className="w-6 h-6 text-[#B94A3E]" strokeWidth={1.5} />
              <h3 className="text-xl" style={{fontFamily: 'Spectral, serif'}}>Upload Your Photo</h3>
            </div>
            <Label htmlFor="photo-upload" className="cursor-pointer">
              <div className="border-2 border-dashed border-[#E5E0D8] rounded-md p-12 text-center hover:bg-[#F9F7F3] transition-colors" data-testid="upload-area">
                <Upload className="w-12 h-12 mx-auto mb-4 text-[#737373]" />
                <p className="text-sm text-[#737373]">{uploading ? 'Uploading...' : 'Click to upload a real photo'}</p>
                <p className="text-xs text-[#737373] mt-2">Only upload photos you own or have permission to use</p>
              </div>
              <Input id="photo-upload" type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} data-testid="photo-upload-input" />
            </Label>
          </div>

          <div className="bg-white border border-[#E5E0D8] rounded-md p-8 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
            <div className="flex items-center gap-3 mb-6">
              <Sparkles className="w-6 h-6 text-[#B94A3E]" strokeWidth={1.5} />
              <h3 className="text-xl" style={{fontFamily: 'Spectral, serif'}}>Generate AI Memory Photo</h3>
            </div>
            <div className="space-y-4">
              <div>
                <Label htmlFor="prompt" className="label-text">Describe a memory-style photo</Label>
                <Textarea
                  id="prompt"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="e.g., A cheerful sunset at the beach"
                  className="mt-2 border-[#E5E0D8] min-h-[100px]"
                  data-testid="photo-prompt-input"
                />
              </div>
              <div className="flex items-start gap-2 p-3 bg-[#F5EBE0]/50 rounded-md">
                <Checkbox id="acknowledge" checked={acknowledged} onCheckedChange={setAcknowledged} data-testid="ai-acknowledge-checkbox" className="mt-1" />
                <Label htmlFor="acknowledge" className="text-xs text-[#1A1A1A]/85 cursor-pointer leading-relaxed">
                  I understand this creates a synthetic image labeled "Made with AI."
                </Label>
              </div>
              <Button
                onClick={handleGenerate}
                className="w-full bg-[#B94A3E] hover:bg-[#9d3e33] text-white"
                disabled={generating || !acknowledged}
                data-testid="generate-photo-btn"
              >
                {generating ? 'Generating...' : 'Generate Memory Photo'}
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E5E0D8] rounded-md p-8 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
          <h3 className="text-2xl font-light tracking-tight mb-6" style={{fontFamily: 'Spectral, serif'}}>Your Photos</h3>
          {loading ? (
            <p className="text-[#737373]">Loading...</p>
          ) : photos.length === 0 ? (
            <div className="text-center py-12" data-testid="no-photos-message">
              <p className="text-[#737373]">No photos yet. Upload or generate your first one!</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {photos.map((photo) => (
                <div key={photo._id} className="relative group" data-testid={`photo-${photo._id}`}>
                  <div className="aspect-square rounded-md overflow-hidden border border-[#E5E0D8]">
                    <img
                      src={`${API}/photos/download/${photo.storage_path}`}
                      alt={photo.original_filename}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {photo.generated && (
                    <div className="absolute top-2 left-2 bg-[#B94A3E] text-white text-xs px-2 py-1 rounded flex items-center gap-1" data-testid={`ai-label-${photo._id}`}>
                      <Sparkles className="w-3 h-3" />
                      Made with AI
                    </div>
                  )}
                  <button
                    onClick={() => handleDelete(photo._id)}
                    className="absolute top-2 right-2 bg-white/90 text-red-600 p-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`delete-photo-${photo._id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
